import React, { FC, useCallback, useEffect, useRef } from 'react'

import { styled } from '@linaria/react'
import { animated, SpringValue } from '@react-spring/web'

import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { useThemeNext } from 'components/designSystem/shared/themes/useThemeNext'
import { colorInterpolate } from 'functions/colorInterpolate'

/**
 * Если передавать x то animated сразу понимает что нужно подставить в
 * transform3d(x 0 0) без интерполяции
 */
export const CanvasWave: FC<{
  x: SpringValue<number>
  _rotate: SpringValue<number>
  hasChangeBodyBackground: boolean
}> = ({ x, _rotate, hasChangeBodyBackground }) => {
  const { colors } = useThemeNext()

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const main = document.getElementById('app-wrapper')
  const requestAnimationFrameId = useRef<number>(0)

  const changeBackgroundColorBody = (offsetX: number) => {
    if (main) {
      if (!hasChangeBodyBackground) {
        main.style.backgroundColor =
          colors.background.page ?? colors.static.white
      } else {
        if (offsetX === 0) {
          main.style.removeProperty('background-color')
        } else {
          main.style.backgroundColor = colorInterpolate(
            offsetX < 0
              ? colors.splash['opacity-default']
              : colors.rose['opacity-default'],
            colors.background.page,
            Math.min(Math.abs(offsetX) / window.innerWidth, 1)
          )
        }
      }
    }
  }

  const render = useCallback((x: number) => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    // Размеры холста
    const width = canvas.width
    const height = canvas.height

    // Очистка канваса
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Начало пути
    ctx.beginPath()
    ctx.moveTo(0, 0)

    /** TODO: подумать над изменением формулы. С учетом сдвига */
    const max = Math.abs(x) / 10

    if (x > 0) {
      // Линии прямоугольника
      ctx.lineTo(width, 0)
      ctx.lineTo(width, height)
      ctx.lineTo(0, height)

      // Первая кривая
      ctx.bezierCurveTo(
        0,
        (height / 10) * 7, // controlPoint1
        max,
        (height / 6) * 4, // controlPoint2
        max,
        height / 2 // endPoint
      )

      // Вторая кривая
      ctx.bezierCurveTo(
        max,
        (height / 6) * 2, // controlPoint1
        0,
        (height / 10) * 3, // controlPoint2
        0,
        0 // endPoint
      )
    } else {
      // Линия к верхнему правому углу
      ctx.lineTo(width, 0)

      // Первая кривая
      ctx.bezierCurveTo(
        width,
        (height / 10) * 3, // controlPoint1
        width - max,
        (height / 6) * 2, // controlPoint2
        width - max,
        height / 2 // endPoint
      )

      // Вторая кривая
      ctx.bezierCurveTo(
        width - max,
        (height / 6) * 4, // controlPoint1
        width,
        (height / 10) * 7, // controlPoint2
        width,
        height // endPoint
      )

      // Линия к нижнему левому углу
      ctx.lineTo(0, height)

      // Замыкаем путь, возвращаясь к начальной точке
      ctx.lineTo(0, 0)
    }

    // Завершение пути
    ctx.closePath()

    // Заливка пути
    ctx.fillStyle = colors.background.page ?? colors.static.white // Цвет заливки
    ctx.fill()
  }, [])

  useEffect(() => {
    if (canvasRef.current) {
      render(0)
    }

    return () => {
      main?.style.removeProperty('background-color')
      cancelAnimationFrame(requestAnimationFrameId.current)
    }
  }, [render, canvasRef, main])

  if (main) {
    return (
      <CanvasCurve
        id="canvas-wave"
        style={{
          transform: x.to((x) => {
            requestAnimationFrameId.current = requestAnimationFrame(() => {
              changeBackgroundColorBody(x)
              render(x)
            })

            return `translateX(${x}px)`
          }),
        }}
        width={main.offsetWidth}
        height={main.offsetHeight}
        ref={canvasRef}
      />
    )
  }

  return null
}

export const CanvasCurveMemo = React.memo(CanvasWave)

const CanvasCurve = styled(animated.canvas)`
  position: fixed;
  left: 0;
  top: 0;
  will-change: transform;
  overflow: hidden;

  @media (min-width: ${breakpoints.tablet}px) {
    display: none;
  }
`
